import { useState } from 'react';
import { toast } from 'sonner';
import { Download, FileSpreadsheet } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import api from '../../lib/api';
import { formatRupiah } from '../../utils/printHelper';

/**
 * Bulk PO import: download template -> upload -> preview -> confirm.
 *
 * The preview (dry_run) never creates anything; PO numbers are sequential and
 * atomic, so nothing may be numbered until the user has seen what the file
 * contains and pressed the confirm button. A group is only created whole —
 * invalid groups are listed with their reasons while clean ones go through.
 */
export function POBulkImportDialog({ open, onOpenChange, onSuccess }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // dry_run response
  const [result, setResult] = useState(null);   // create response

  const reset = () => { setFile(null); setPreview(null); setResult(null); };

  const handleOpenChange = (isOpen) => {
    if (!isOpen) reset();
    onOpenChange(isOpen);
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get('/purchase-orders/bulk-template', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'template_import_po.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Gagal mengunduh template');
    }
  };

  const upload = async (dryRun) => {
    if (!file) { toast.error('Pilih file template yang sudah diisi'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/purchase-orders/bulk-import?dry_run=${dryRun}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (dryRun) {
        setPreview(res.data);
        setResult(null);
      } else {
        setResult(res.data);
        setPreview(null);
        const { created, failed } = res.data.summary;
        if (created > 0) toast.success(`${created} PO berhasil dibuat${failed ? `, ${failed} gagal` : ''}`);
        else toast.error('Tidak ada PO yang dibuat');
        if (created > 0 && onSuccess) onSuccess();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal memproses file');
    } finally {
      setBusy(false);
    }
  };

  const validCount = preview?.summary?.valid || 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import PO dari Excel</DialogTitle>
          <p className="text-sm text-slate-600">
            Satu baris = satu item; kolom Grup PO menyatukan baris menjadi satu PO (full maupun partial).
          </p>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="bulk-po-template-btn">
              <FileSpreadsheet className="h-4 w-4 mr-2" /> Unduh Template
            </Button>
            <span className="text-xs text-slate-500">
              Template memuat dropdown supplier, perusahaan, dan Daftar Barang terkini.
            </span>
          </div>

          <div className="border-2 border-dashed rounded-lg p-4 flex items-center gap-3">
            <FileSpreadsheet className="h-8 w-8 text-emerald-600 shrink-0" />
            <div className="flex-1">
              <input
                type="file"
                accept=".xlsx"
                onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setResult(null); }}
                data-testid="bulk-po-file-input"
              />
              {file && <p className="text-xs text-slate-500 mt-1">{file.name}</p>}
            </div>
            <Button onClick={() => upload(true)} disabled={!file || busy} data-testid="bulk-po-preview-btn">
              <Download className="h-4 w-4 mr-2" /> {busy && !preview ? 'Memproses...' : 'Pratinjau'}
            </Button>
          </div>

          {preview && (
            <div className="space-y-3">
              {preview.file_errors?.length > 0 && (
                <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm text-rose-700">
                  {preview.file_errors.map((e, i) => <p key={i}>{e}</p>)}
                </div>
              )}
              {preview.groups?.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="px-3 py-2 text-left">Grup</th>
                        <th className="px-3 py-2 text-left">Supplier</th>
                        <th className="px-3 py-2 text-left">Jenis</th>
                        <th className="px-3 py-2 text-right">Item</th>
                        <th className="px-3 py-2 text-right">Total</th>
                        <th className="px-3 py-2 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.groups.map((g) => (
                        <tr key={g.grup} className="border-t align-top">
                          <td className="px-3 py-2 font-mono">{g.grup}</td>
                          <td className="px-3 py-2">{g.supplier}</td>
                          <td className="px-3 py-2">{g.jenis}</td>
                          <td className="px-3 py-2 text-right">{g.item_count}</td>
                          <td className="px-3 py-2 text-right font-mono">Rp {formatRupiah(g.total)}</td>
                          <td className="px-3 py-2">
                            {g.valid ? (
                              <span className="text-emerald-700 text-xs font-semibold bg-emerald-50 px-2 py-0.5 rounded">Siap dibuat</span>
                            ) : (
                              <div>
                                <span className="text-rose-700 text-xs font-semibold bg-rose-50 px-2 py-0.5 rounded">Bermasalah</span>
                                <ul className="mt-1 text-xs text-rose-600 list-disc pl-4">
                                  {g.errors.map((e, i) => <li key={i}>{e}</li>)}
                                </ul>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-600">
                  {validCount} dari {preview.summary.total_groups} PO siap dibuat.
                  {preview.summary.invalid > 0 && ' Grup bermasalah tidak akan dibuat.'}
                </p>
                <Button
                  onClick={() => upload(false)}
                  disabled={busy || validCount === 0}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="bulk-po-confirm-btn"
                >
                  {busy ? 'Membuat...' : `Buat ${validCount} PO`}
                </Button>
              </div>
            </div>
          )}

          {result && (
            <div className="space-y-2">
              {result.created?.map((c) => (
                <p key={c.po_number} className="text-sm text-emerald-700">
                  ✓ Grup {c.grup} → <span className="font-mono font-semibold">{c.po_number}</span> (Rp {formatRupiah(c.total)})
                </p>
              ))}
              {result.failed?.map((f, i) => (
                <div key={i} className="text-sm text-rose-700">
                  ✗ Grup {f.grup}:
                  <ul className="list-disc pl-5 text-xs">{f.errors.map((e, j) => <li key={j}>{e}</li>)}</ul>
                </div>
              ))}
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => handleOpenChange(false)}>Tutup</Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
